import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchTeam, failureCode, type TeamView } from "../services/ccTenantViewService";
import { Card, Kpi, PageHeader, StateBox, hours } from "../components/ui";

// Equipo de Coding Cloud asignado a ESTE cliente (CC-CONSOLE #979 / #981): puesto,
// porcentaje y horas por semana. Nunca sueldos ni datos personales. Solo lectura.

export default function TeamClientView() {
  const { t } = useTranslation("common");
  const [view, setView] = useState<TeamView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setView(await fetchTeam());
    } catch (err) {
      setView(null);
      setError(failureCode(err) ?? "generic");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const role = (r: string | null) => t(`clientView.roles.${r ?? "other"}`, { defaultValue: r ?? "—" });

  return (
    <div className="w-full max-w-6xl space-y-4 overflow-y-auto p-4 sm:p-6">
      <PageHeader title={t("clientView.team.title")} subtitle={t("clientView.team.subtitle", { client: view?.client.name ?? "" })} />
      <StateBox loading={loading} error={error} onRetry={() => void load()} />

      {view && (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
            <Kpi label={t("clientView.team.headcount")} value={String(view.headcount)} />
            <Kpi label={t("clientView.team.weeklyHours")} value={hours(view.weeklyHours)} />
            <Kpi
              label={t("clientView.team.byRole")}
              value={String(Object.keys(view.byRole).length)}
              hint={Object.entries(view.byRole)
                .map(([r, n]) => `${role(r)}: ${n}`)
                .join(" · ")}
            />
          </div>
          <Card title={t("clientView.team.members")}>
            {view.members.length === 0 ? (
              <p className="text-xs text-content-secondary">{t("clientView.empty")}</p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-content-secondary">
                    <th className="py-1">{t("clientView.team.name")}</th>
                    <th className="py-1">{t("clientView.team.role")}</th>
                    <th className="py-1">{t("clientView.team.allocation")}</th>
                    <th className="py-1 text-right">{t("clientView.team.hoursPerWeek")}</th>
                  </tr>
                </thead>
                <tbody>
                  {view.members.map((m, i) => (
                    <tr key={`${m.name}-${i}`} className="border-t border-line text-content">
                      <td className="py-1.5 font-medium">{m.name ?? "—"}</td>
                      <td className="py-1.5">{role(m.role)}</td>
                      <td className="py-1.5 text-xs">
                        {m.allocations.map((a) => `${a.projectName ?? "—"} ${a.percentage}%`).join(", ")}
                      </td>
                      <td className="py-1.5 text-right tabular-nums">{hours(m.weeklyHours)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>
          {!view.shareTeam && <p className="text-xs text-content-secondary">{t("clientView.anonymized")}</p>}
        </>
      )}
    </div>
  );
}
